import { useTranslation } from "react-i18next";

import type { PlanDayCard, PlanSessionCard } from "./WeekView";
import { DotStatus } from "../canon/DotStatus";
import type { DotTone } from "../canon/DotStatus";

/**
 * ListView — Cycle 18 P1.2 rewrite to canon 7-col table.
 *
 * Mockup: docs/design/claude-design/desktop/athlete/Storica Desktop - Plan.dc.html
 *
 * Structure (canon):
 *   • 7-column grid: 80px DAY · 80px DATE · 60px SPORT · 1fr WORKOUT · 90px VOLUME · 100px INTENSITY · 60px TSS
 *   • Padding 14px 18px per row, gap 14
 *   • Header row: BG.s background + borderBottom hairline + Mono 9/0.18em INK.m
 *   • Rows: borderTop hairline, today bg iris/.05, race bg green/.05
 *   • Day cell: Mono 10/0.18em color = SEM.g (race) / IRIS.l (today) / INK.s
 *   • Date cell: Onest 14/700 INK.p
 *   • Workout cell: Onest 13/600 INK.p (truncate) + secondary Mono 9/0.06em INK.m
 *   • Volume cell: tabular-num 13/700 INK.p
 *   • Intensity badge: Mono 9.5/0.18em, цвет — из intensity.ts (не отсюда:
 *     описание цвета в шапке компонента и есть та копия, из которой он
 *     потом заводится заново)
 *   • TSS cell: tabular-num 14/700 INK.p text-right
 *   • Rest days render как row с opacity .55 + "Отдых" placeholder
 */
export interface ListViewProps {
  dayCards: PlanDayCard[];
  onSessionClick?: (session: PlanSessionCard) => void;
  className?: string;
}

// Canon section 7 discipline palette → DotStatus tone. Folds the long
// taxonomy (run/gravel/trail/triathlon/...) onto the 8 canon tones.
const SPORT_TONE: Record<string, DotTone> = {
  run: "run",
  trail: "ok",         // canon: trail = green family
  gravel: "run",
  cycle: "bike",
  bike: "bike",
  ride: "bike",
  triathlon: "bike",
  duathlon: "warn",
  hyrox: "bad",
  swim: "swim",
  strength: "muted",
  other: "muted",
};

function sportTone(discipline: string): DotTone {
  return SPORT_TONE[discipline.toLowerCase()] ?? "muted";
}

import { intensityChip } from "./intensity";

// Вид чипа интенсивности целиком живёт в intensity.ts — включая геометрию.
// Здесь стояла своя таблица токенов, красившая инлайн-стилем мимо
// семантического слоя; разбор в шапке intensity.ts.

const GRID_COLS = "80px 80px 60px 1fr 90px 100px 60px";
const HAIR = "1px solid rgba(230,232,255,.06)";

export function ListView({ dayCards, onSessionClick, className = "" }: ListViewProps) {
  const { t } = useTranslation("plan");

  // Flatten — each session is its own row; rest days kept as placeholder rows.
  const rows = dayCards.flatMap((day) => {
    if (day.sessions.length === 0) {
      return [{ day, session: null as PlanSessionCard | null, key: `rest-${day.weekday}` }];
    }
    return day.sessions.map((s, idx) => ({
      day,
      session: s,
      key: `${day.weekday}-${s.id}-${idx}`,
    }));
  });

  if (rows.length === 0) {
    return (
      <div
        data-testid="list-view-empty"
        className="rounded-card px-4 py-8 text-center text-sm"
        style={{
          background: "var(--bg-card)",
          color: "var(--fg-muted)",
        }}
      >
        {t("list_view.empty", {
          defaultValue: "На этой неделе нет запланированных тренировок.",
        })}
      </div>
    );
  }

  // W-REBUILD-6: 390px без компромиссов (мандат раздел 5) — на <768px таблица
  // сворачивается в двухстрочные строки, никакого горизонтального скролла.
  // Порог md, не sm: фиксированным колонкам сетки нужно ~590px, на 640–700px
  // колонке «Тренировка» оставалось <60px — имя резалось до одной буквы.
  const mobileList = (
    <div
      data-testid="list-view-mobile"
      className="md:hidden"
      style={{
        background: "var(--bg-card)",
        borderRadius: 11,
        borderTop: HAIR,
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,.06), 0 8px 22px rgba(0,0,0,.42)",
      }}
    >
      {rows.map(({ day, session, key }) => {
        const isToday = !!day.isToday;
        const isRace = !!day.isRace || !!session?.isRace;
        const restRow = session == null;
        const band = session ? intensityChip(session.difficultyLevel ?? null) : null;
        const dayColor = isRace ? "var(--ok)" : isToday ? "var(--accent)" : "var(--fg-muted)";
        return (
          <div
            key={`m-${key}`}
            role="row"
            data-testid={
              restRow
                ? `list-view-m-rest-${day.weekday}`
                : `list-view-m-session-${session!.id}`
            }
            onClick={
              session && onSessionClick ? () => onSessionClick(session) : undefined
            }
            className={
              session && onSessionClick
                ? // active: — тап-отклик: на мобиле hover не существует, и без
                  // видимой реакции нажатие на строку «немое».
                  "cursor-pointer hover:bg-bg-raised/40 active:bg-bg-raised/70 transition-colors"
                : ""
            }
            style={{
              padding: "12px 14px",
              borderTop: HAIR,
              opacity: restRow ? 0.55 : 1,
              background: isRace
                ? "color-mix(in srgb, var(--ok) 5%, transparent)"
                : isToday
                  ? "rgb(var(--accent-rgb) / .05)"
                  : "transparent",
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="indigo-label shrink-0" style={{ color: dayColor }}>
                {day.weekdayLabel} {day.date}
              </span>
              {session && (
                <DotStatus tone={sportTone(session.sport)} size={8} alpha={1} />
              )}
              <span
                className="truncate"
                style={{
                  fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  color: session ? "var(--fg-primary)" : "var(--fg-muted)",
                }}
              >
                {session ? session.title : t("list_view.rest", { defaultValue: "Отдых" })}
              </span>
            </div>
            {session && (session.volumeLabel || session.tss != null || band) && (
              <div className="flex items-center gap-3 mt-1 pl-0.5">
                {session.volumeLabel && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fg-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {session.volumeLabel}
                  </span>
                )}
                {session.tss != null && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--fg-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {Math.round(session.tss)} TSS
                  </span>
                )}
                {band && (
                  <span className={band.className}>
                    {t(`intensity.${band.level.toLowerCase()}`, { defaultValue: band.level })}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={`w-full ${className}`}>
      {mobileList}
      <div
      data-testid="list-view"
      className="overflow-x-auto hidden md:block"
      style={{
        background: "var(--bg-card)",
        borderRadius: 11,
        borderTop: HAIR,
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,.06), 0 8px 22px rgba(0,0,0,.42)",
      }}
    >
      {/* Header row */}
      <div
        role="row"
        className="grid items-center"
        style={{
          gridTemplateColumns: GRID_COLS,
          padding: "14px 18px",
          background: "var(--bg-base)",
          borderBottom: HAIR,
          gap: 14,
        }}
      >
        {[
          { key: "day", label: t("list_view.col_day", { defaultValue: "ДЕНЬ" }), align: "center" },
          { key: "date", label: t("list_view.col_date", { defaultValue: "ДАТА" }), align: "center" },
          { key: "sport", label: t("list_view.col_sport", { defaultValue: "СПОРТ" }), align: "center" },
          { key: "workout", label: t("list_view.col_workout", { defaultValue: "ТРЕНИРОВКА" }), align: "left" },
          { key: "volume", label: t("list_view.col_volume", { defaultValue: "ОБЪЁМ" }), align: "right" },
          { key: "intensity", label: t("list_view.col_intensity", { defaultValue: "ИНТЕНС" }), align: "center" },
          { key: "tss", label: t("list_view.col_tss", { defaultValue: "TSS" }), align: "right" },
        ].map((h) => (
          <div
            key={h.key}
            role="columnheader"
            className="indigo-label"
            style={{
              textAlign: h.align as "left" | "right" | "center",
            }}
          >
            {h.label}
          </div>
        ))}
      </div>

      {/* Data rows */}
      {rows.map(({ day, session, key }) => {
        const isToday = !!day.isToday;
        const isRace = !!day.isRace || !!session?.isRace;
        const restRow = session == null;
        const band = session ? intensityChip(session.difficultyLevel ?? null) : null;
        const dayColor = isRace ? "var(--ok)" : isToday ? "var(--accent)" : "var(--fg-muted)";

        return (
          <div
            key={key}
            role="row"
            data-testid={
              restRow ? `list-view-rest-${day.weekday}` : `list-view-session-${session!.id}`
            }
            onClick={
              session && onSessionClick
                ? () => onSessionClick(session)
                : undefined
            }
            className={`grid items-center transition-colors ${
              session && onSessionClick ? "cursor-pointer hover:bg-bg-raised/40" : ""
            }`}
            style={{
              gridTemplateColumns: GRID_COLS,
              padding: "14px 18px",
              borderTop: HAIR,
              gap: 14,
              opacity: restRow ? 0.55 : 1,
              background: isRace
                ? "color-mix(in srgb, var(--ok) 5%, transparent)"
                : isToday
                  ? "rgb(var(--accent-rgb) / .05)"
                  : "transparent",
            }}
          >
            {/* col 1: day label */}
            <div
              role="cell"
              className="indigo-label"
              style={{ color: dayColor, textAlign: "center" }}
            >
              {day.weekdayLabel}
            </div>

            {/* col 2: date */}
            <div role="cell" style={{ textAlign: "center" }}>
              <span
                style={{
                  fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--fg-primary)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {day.date}
              </span>
            </div>

            {/* col 3: sport dot (canon section 9 DotStatus 8px @ .45) */}
            <div role="cell" style={{ display: "flex", justifyContent: "center" }}>
              {session ? (
                <DotStatus tone={sportTone(session.sport)} size={8} alpha={1} />
              ) : (
                <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>—</span>
              )}
            </div>

            {/* col 4: workout name */}
            <div role="cell" className="min-w-0">
              {session ? (
                <>
                  <div
                    style={{
                      fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--fg-primary)",
                      letterSpacing: "-0.01em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {session.title}
                  </div>
                  {session.zone && (
                    <div
                      style={{
                        fontFamily:
                          '"JetBrains Mono", "ui-monospace", monospace',
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        color: "var(--fg-muted)",
                        marginTop: 2,
                      }}
                    >
                      {session.zone.toUpperCase()}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>
                  {t("list_view.rest", { defaultValue: "Отдых" })}
                </span>
              )}
            </div>

            {/* col 5: volume */}
            <div role="cell" style={{ textAlign: "right" }}>
              {session?.volumeLabel ? (
                <span
                  style={{
                    fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--fg-primary)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {session.volumeLabel}
                </span>
              ) : (
                <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>—</span>
              )}
            </div>

            {/* col 6: intensity badge */}
            <div role="cell" style={{ textAlign: "center" }}>
              {band ? (
                <span
                  data-testid={
                    session ? `list-view-intensity-${session.id}` : undefined
                  }
                  className={band.className}
                >
                  {t(`intensity.${band.level.toLowerCase()}`, { defaultValue: band.level })}
                </span>
              ) : (
                <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>—</span>
              )}
            </div>

            {/* col 7: TSS */}
            <div role="cell" style={{ textAlign: "right" }}>
              {session?.tss != null ? (
                <span
                  style={{
                    fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--fg-primary)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Math.round(session.tss)}
                </span>
              ) : (
                <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>—</span>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
